<template>
  <div>
    <!-- Group headers -->
    <div
      v-for="group in projectGroups"
      :key="group.date"
      class="mb-6"
    >
      <h3 class="text-lg font-semibold text-gray-800 mb-3">
        {{ group.date }}
      </h3>
            
      <!-- Timeline View -->
      <div class="timeline-container flex">
        <!-- Time column -->
        <div class="time-column w-16 flex-shrink-0 pr-2">
          <div
            v-for="time in timeSlots"
            :key="`${group.date}-${time.label}`" 
            class="time-slot" 
            :style="{ height: `${timeSlotHeight}px` }"
          >
            <span class="text-xs text-gray-500">{{ time.label }}</span>
          </div>
        </div>
                
        <!-- Jobs column -->
        <div class="jobs-column flex-grow relative">
          <!-- Time slot dividers -->
          <div
            v-for="time in timeSlots"
            :key="`divider-${group.date}-${time.label}`" 
            class="time-divider border-t border-gray-200" 
            :style="{ top: `${time.position}px` }"
          />
                    
          <!-- Project cards positioned based on time -->
          <div
            v-for="timeSlot in getGroupedProjects(group.projects)"
            :key="`${group.date}-${timeSlot.time}`" 
            class="absolute w-full transition-all duration-200 px-1"
            :style="{ top: `${timeSlot.position}px` }"
          >
            <!-- Multiple projects in the same time slot -->
            <div class="flex gap-2 w-full">
              <Card
                v-for="(project, projectIndex) in timeSlot.projects"
                :key="`${timeSlot.time}-${projectIndex}`"
                class="cursor-pointer hover:shadow-lg transition-shadow duration-200 border border-gray-200"
                :class="{ 'w-full': timeSlot.projects.length === 1, 'w-1/2': timeSlot.projects.length === 2, 'w-1/3': timeSlot.projects.length === 3, 'w-1/4': timeSlot.projects.length >= 4 }"
                @click="navigateToProject(project)"
              >
                <template #content>
                  <div class="flex justify-between items-center">
                    <div class="flex-1">
                      <div class="flex items-center gap-4">
                        <div class="flex-1">
                          <div class="flex items-center gap-2">
                            <span class="font-bold">{{ project.time }}</span>
                            <Badge
                              :value="project.status"
                              :severity="projectSeverity(project.status)"
                            />
                          </div>
                          <h3 class="text-lg font-semibold text-gray-800 mt-1">
                            {{ project.projectName }}
                          </h3>
                          <div class="flex items-center gap-2 mt-1">
                            <i class="pi pi-building text-gray-500" />
                            <span class="text-sm text-gray-600">{{ project.client }}</span>
                          </div>
                          <p class="text-sm text-gray-500 mt-1">
                            {{ project.projectNumber }}
                          </p>
                        </div>
                        <div class="flex items-center gap-6">
                          <div class="flex items-center gap-2">
                            <i class="pi pi-box text-gray-500" />
                            <span class="text-sm text-gray-600">{{ project.totalItems }} items</span>
                          </div>
                          <div class="flex items-center gap-2">
                            <i class="pi pi-user text-gray-500" />
                            <span class="text-sm text-gray-600">{{ project.projectManager }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="mt-3">
                        <label class="text-sm text-gray-600 mb-1 block">Progress</label>
                        <ProgressBar
                          :value="calcProgress(project)"
                          class="h-2"
                        />
                      </div>
                    </div>
                  </div>
                </template>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps } from 'vue';
import { projectSeverity } from '@/shared/DataTableFunctions';
import { useRouter } from 'vue-router';

const router = useRouter();

// Define props for projects
const props = defineProps({
    projectsData: {
        type: Array,
        default: () => []
    }
});

// Timeline configuration
const startHour = 7; // 7 AM
const endHour = 19; // 7 PM
const timeSlotHeight = 80; // height in pixels for each hour

// Generate time slots for the timeline
const timeSlots = computed(() => {
    const slots = [];
    for (let hour = startHour; hour <= endHour; hour++) {
        const position = (hour - startHour) * timeSlotHeight;
        slots.push({
            hour,
            label: `${hour % 12 || 12}${hour < 12 ? 'am' : 'pm'}`,
            position
        });
    }
    return slots;
});

// Calculate position for a project based on its time
const getProjectPosition = (project) => {
    try {
        // Parse the time (format: "HH:mm")
        const [hours, minutes] = project.time.split(':').map(Number);
        
        // Calculate position
        const hourPosition = (hours - startHour) * timeSlotHeight;
        const minuteOffset = (minutes / 60) * timeSlotHeight;
        
        return hourPosition + minuteOffset;
    } catch (e) {
        console.error("Error calculating project position:", e);
        return 0; // Fallback to top position
    }
};

// Fallback project data if no props are provided
const fallbackProjects = [
    {
        id: 1,
        status: 'confirmed',
        time: '08:30',
        start: 'Today',
        projectNumber: 'PRJ2405',
        projectName: 'Music Festival Setup',
        projectManager: 'Sarah Johnson',
        totalItems: 120,
        totalPrepped: 105,
        client: 'SoundWave Events'
    },
    {
        id: 2,
        status: 'confirmed',
        time: '10:15',
        start: 'Today',
        projectNumber: 'PRJ2407',
        projectName: 'Corporate Conference',
        projectManager: 'Michael Chen',
        totalItems: 75,
        totalPrepped: 50,
        client: 'TechCorp Inc.'
    },
    {
        id: 3,
        status: 'draft',
        time: '14:00',
        start: 'Today',
        projectNumber: 'PRJ2409',
        projectName: 'Wedding Reception',
        projectManager: 'Emma Davis',
        totalItems: 30,
        totalPrepped: 5,
        client: 'Private Client'
    },
    {
        id: 4,
        status: 'confirmed',
        time: '09:00',
        start: 'Tomorrow',
        projectNumber: 'PRJ2411',
        projectName: 'Theater Production',
        projectManager: 'James Wilson',
        totalItems: 90,
        totalPrepped: 85,
        client: 'City Arts Center'
    },
    {
        id: 5,
        status: 'draft',
        time: '13:30',
        start: 'Tomorrow',
        projectNumber: 'PRJ2413',
        projectName: 'Charity Fundraiser',
        projectManager: 'Olivia Martinez',
        totalItems: 45,
        totalPrepped: 10,
        client: 'Hope Foundation'
    },
    {
        id: 6,
        status: 'confirmed',
        time: '08:00',
        start: 'Next Monday',
        projectNumber: 'PRJ2415',
        projectName: 'Product Launch Event',
        projectManager: 'David Kim',
        totalItems: 110,
        totalPrepped: 90,
        client: 'Innovate Products'
    },
    {
        id: 7,
        status: 'confirmed',
        time: '10:30',
        start: 'Next Monday',
        projectNumber: 'PRJ2419',
        projectName: 'College Graduation',
        projectManager: 'Alex Thompson',
        totalItems: 85,
        totalPrepped: 0,
        client: 'State University'
    },
    {
        id: 8,
        status: 'draft',
        time: '09:15',
        start: 'Next Tuesday',
        projectNumber: 'PRJ2422',
        projectName: 'Fashion Show',
        projectManager: 'Sophia Lee',
        totalItems: 60,
        totalPrepped: 0,
        client: 'Style Media Group'
    },
    {
        id: 9,
        status: 'draft',
        time: '11:00',
        start: 'Next Wednesday',
        projectNumber: 'PRJ2427',
        projectName: 'Sports Tournament',
        projectManager: 'Ryan Garcia',
        totalItems: 150,
        totalPrepped: 0,
        client: 'City Athletics Department'
    }
];

// Use prop data if available, otherwise use fallback data
const projects = computed(() => {
    return props.projectsData && props.projectsData.length > 0 
        ? props.projectsData 
        : fallbackProjects;
});

// Group projects by date
const projectGroups = computed(() => {
    const groups = {};
    
    projects.value.forEach(project => {
        if (!groups[project.start]) {
            groups[project.start] = {
                date: project.start,
                projects: []
            };
        }
        groups[project.start].projects.push(project);
    });
    
    // Convert to array and sort by date
    return Object.values(groups);
});

const calcProgress = (data) => {
    const { totalItems, totalPrepped } = data;
    return (totalPrepped / totalItems) * 100;
};

const navigateToProject = (equipmentList) => {
    // The API returns equipment lists, not projects
    // Each equipment list has a project_id and an equipment_list id
    const projectId = equipmentList.projectId || equipmentList.project_id;
    const equipmentListId = equipmentList.equipmentListId || equipmentList.id;
    
    if (projectId && equipmentListId) {
        router.push(`/warehouse-project/${projectId}/prepared/${equipmentListId}`);
    } else {
        console.error('Missing project ID or equipment list ID:', equipmentList);
    }
};

// Group projects by time within each group
const getGroupedProjects = (projects) => {
    const grouped = {};
    
    // Group projects by their time
    projects.forEach(project => {
        if (!grouped[project.time]) {
            grouped[project.time] = [];
        }
        grouped[project.time].push(project);
    });
    
    // Convert the object to an array of time slots with projects
    return Object.entries(grouped).map(([time, projectsInSlot]) => ({
        time,
        projects: projectsInSlot,
        position: getProjectPosition({ time })
    })).sort((a, b) => a.position - b.position);
};
</script>

<style scoped>
.timeline-container {
    min-height: 800px;
    position: relative;
    margin-bottom: 40px;
}

.time-column {
    position: relative;
    z-index: 10;
}

.time-slot {
    position: relative;
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
}

.jobs-column {
    overflow-y: visible;
}

.time-divider {
    position: absolute;
    width: 100%;
    z-index: 5;
}
</style>
